import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { memo, useState, useEffect, useCallback } from 'react';
import { useTranslation } from '@eruvaka/web-pondlogs-i18n';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { fetchAdvancedRules, toggleAdvancedRule, deleteAdvancedRule, } from '../../api/preferencesApi';
import { RuleFormDialog } from './RuleFormDialog';
import { colorTokens } from '@eruvaka/web-pondlogs-base';
export const AdvancedRulesSection = memo(() => {
    const { t } = useTranslation('main');
    const [rules, setRules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingRule, setEditingRule] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const loadRules = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await fetchAdvancedRules();
            setRules(data);
        }
        catch (err) {
            setError('errors.loadRulesFailed');
        }
        finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => {
        loadRules();
    }, [loadRules]);
    const handleToggle = async (ruleId, isActive) => {
        try {
            await toggleAdvancedRule(ruleId, isActive);
            setRules((prev) => prev.map((r) => (r.ruleId === ruleId ? { ...r, isActive } : r)));
        }
        catch (err) {
            setError('errors.toggleRuleFailed');
        }
    };
    const handleDelete = async () => {
        if (!deleteTarget)
            return;
        try {
            await deleteAdvancedRule(deleteTarget);
            setRules((prev) => prev.filter((r) => r.ruleId !== deleteTarget));
            setDeleteTarget(null);
        }
        catch (err) {
            setError('errors.deleteRuleFailed');
        }
    };
    const handleDialogClose = useCallback(async (saved) => {
        setDialogOpen(false);
        setEditingRule(null);
        if (saved)
            await loadRules();
    }, [loadRules]);
    if (loading) {
        return (_jsx(Box, { sx: { display: 'flex', justifyContent: 'center', py: 2 }, children: _jsx(CircularProgress, { size: 24 }) }));
    }
    return (_jsxs(Box, { children: [error && (_jsx(Alert, { severity: "error", sx: { mb: 2 }, onClose: () => setError(null), children: t(error) })), _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }, children: [_jsxs(Typography, { sx: { fontSize: 11, color: colorTokens.gray600 }, children: [rules.filter((r) => r.isActive).length, " active \u00B7", ' ', rules.filter((r) => !r.isActive).length, " disabled"] }), _jsx(Button, { variant: "contained", startIcon: _jsx(AddIcon, {}), size: "small", onClick: () => {
                            setEditingRule(null);
                            setDialogOpen(true);
                        }, sx: {
                            background: colorTokens.primary,
                            borderRadius: 2,
                            fontSize: 11,
                            textTransform: 'none',
                            '&:hover': { background: colorTokens.primaryDark },
                        }, children: t('advancedRules.newRule') })] }), _jsx(TableContainer, { sx: { border: `1px solid ${colorTokens.gray200}`, borderRadius: 2, overflow: 'hidden' }, children: _jsxs(Table, { size: "small", children: [_jsx(TableHead, { children: _jsx(TableRow, { sx: { background: colorTokens.gray50 }, children: [
                                    '#',
                                    t('advancedRules.columnRuleName'),
                                    t('advancedRules.columnConditions'),
                                    t('advancedRules.columnConfirmMessage'),
                                    t('advancedRules.columnStatus'),
                                    t('advancedRules.columnActions'),
                                ].map((h) => (_jsx(TableCell, { sx: {
                                        fontWeight: 700,
                                        fontSize: 11,
                                        color: colorTokens.gray600,
                                        whiteSpace: 'nowrap',
                                    }, children: h }, h))) }) }), _jsxs(TableBody, { children: [rules.length === 0 && (_jsx(TableRow, { children: _jsx(TableCell, { colSpan: 6, sx: { textAlign: 'center', py: 4, color: colorTokens.gray600, fontSize: 11 }, children: t('advancedRules.noRulesMessage') }) })), rules.map((rule, i) => (_jsxs(TableRow, { hover: true, children: [_jsx(TableCell, { sx: { width: 40 }, children: _jsx(Box, { sx: {
                                                    width: 22,
                                                    height: 22,
                                                    borderRadius: '50%',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: 10,
                                                    fontWeight: 700,
                                                    background: rule.isActive
                                                        ? `${colorTokens.primary}12`
                                                        : `${colorTokens.gray600}10`,
                                                    color: rule.isActive ? colorTokens.primary : colorTokens.gray600,
                                                }, children: i + 1 }) }), _jsx(TableCell, { children: _jsx(Typography, { sx: {
                                                    fontSize: 12,
                                                    fontWeight: 600,
                                                    color: rule.isActive ? colorTokens.gray800 : colorTokens.gray600,
                                                }, children: rule.description }) }), _jsx(TableCell, { sx: { maxWidth: 200 }, children: rule.conditions.map((c, ci) => (_jsxs(Box, { sx: {
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 0.5,
                                                    mb: 0.5,
                                                    flexWrap: 'wrap',
                                                }, children: [ci > 0 && (_jsx(Box, { component: "span", sx: {
                                                            fontSize: 9,
                                                            fontWeight: 700,
                                                            px: 0.75,
                                                            py: 0.25,
                                                            borderRadius: 0.5,
                                                            background: `${colorTokens.primary}12`,
                                                            color: colorTokens.primary,
                                                        }, children: c.logicalOperator })), _jsxs(Box, { component: "span", sx: {
                                                            fontSize: 10,
                                                            px: 0.75,
                                                            py: 0.25,
                                                            borderRadius: 0.5,
                                                            background: colorTokens.gray50,
                                                            color: colorTokens.gray800,
                                                        }, children: [c.parameter, " ", c.operator, ' ', c.operator === 'BETWEEN'
                                                                ? `${c.minValue ?? ''} – ${c.maxValue ?? ''}`
                                                                : String(c.value ?? '')] })] }, ci))) }), _jsx(TableCell, { sx: { maxWidth: 200 }, children: _jsx(Typography, { sx: { fontSize: 10, color: colorTokens.gray600, lineHeight: 1.5 }, children: rule.confirmationMessage }) }), _jsx(TableCell, { children: _jsx(Switch, { size: "small", checked: rule.isActive, onChange: (e) => handleToggle(rule.ruleId, e.target.checked), sx: {
                                                    '& .MuiSwitch-switchBase.Mui-checked': { color: colorTokens.primary },
                                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                        backgroundColor: colorTokens.primary,
                                                    },
                                                } }) }), _jsx(TableCell, { children: _jsxs(Box, { sx: { display: 'flex', gap: 0.5 }, children: [_jsx(IconButton, { size: "small", onClick: () => {
                                                            setEditingRule(rule);
                                                            setDialogOpen(true);
                                                        }, "aria-label": "edit rule", children: _jsx(EditOutlinedIcon, { sx: { fontSize: 14, color: colorTokens.gray600 } }) }), _jsx(IconButton, { size: "small", onClick: () => setDeleteTarget(rule.ruleId), "aria-label": "delete rule", children: _jsx(DeleteOutlineIcon, { sx: { fontSize: 14, color: colorTokens.error } }) })] }) })] }, rule.ruleId)))] })] }) }), _jsxs(Dialog, { open: !!deleteTarget, onClose: () => setDeleteTarget(null), maxWidth: "xs", fullWidth: true, children: [_jsxs(DialogTitle, { sx: { display: 'flex', alignItems: 'center', gap: 1, fontSize: 14, fontWeight: 700 }, children: [_jsx(Box, { sx: {
                                    width: 32,
                                    height: 32,
                                    borderRadius: 2,
                                    background: `${colorTokens.error}15`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }, children: _jsx(WarningAmberIcon, { sx: { fontSize: 16, color: colorTokens.error } }) }), t('advancedRules.deleteTitle')] }), _jsx(DialogContent, { children: _jsx(Typography, { sx: { fontSize: 12, color: colorTokens.gray600 }, children: t('advancedRules.deleteBody') }) }), _jsxs(DialogActions, { sx: { px: 3, pb: 2.5, gap: 1 }, children: [_jsx(Button, { onClick: () => setDeleteTarget(null), variant: "outlined", size: "small", sx: {
                                    fontSize: 11,
                                    borderColor: colorTokens.gray200,
                                    color: colorTokens.gray600,
                                    textTransform: 'none',
                                    borderRadius: 2,
                                }, children: t('advancedRules.cancelButton') }), _jsx(Button, { onClick: handleDelete, variant: "contained", size: "small", sx: {
                                    fontSize: 11,
                                    background: colorTokens.error,
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    '&:hover': { background: colorTokens.error },
                                }, children: t('advancedRules.deleteButton') })] })] }), _jsx(RuleFormDialog, { open: dialogOpen, rule: editingRule, onClose: handleDialogClose })] }));
});
AdvancedRulesSection.displayName = 'AdvancedRulesSection';
